import { useState } from "react";
import { useReportingResource } from "@/hooks/useReportingResource";
import { Button } from "@/components/ui/button";
import { Archive, GitBranch, FileCheck, Lock } from "lucide-react";
import EvidencePanel from "@/pages/modules/EvidencePanel";
import { RecordDialog } from "@/pages/modules/forms";

export default function ArchiveCenter({ id }) {
  return <ArchiveWorkspace key={id} id={id} />;
}

function ArchiveWorkspace({ id }) {
  const binderResource=useReportingResource(id,"binder");
  const docsResource=useReportingResource(id,"report-documents");
  const amendsResource=useReportingResource(id,"post-archive-amendments");
  const binder=binderResource.data, docs=docsResource.data || [], amends=amendsResource.data || [];
  const [dlg, setDlg] = useState(null);
  const busy=binderResource.busy || docsResource.busy || amendsResource.busy;
  const createDoc = async () => {
    const response=await docsResource.mutate("post",`/engagements/${id}/report-documents`,{doc_type:"Laporan Keuangan"});
    if(response) await binderResource.reload();
  };
  const advance = async (rid,status) => {
    const response=await docsResource.mutate("post",`/report-documents/${rid}/status`,{status});
    if(response) await binderResource.reload();
  };
  const doArchive = async () => {
    const response=await binderResource.mutate("post",`/engagements/${id}/archive`,undefined,"Perikatan diarsipkan & dikunci");
    if(response) await Promise.all([docsResource.reload(),amendsResource.reload()]);
  };
  const amend = async (v) => {
    const response=await amendsResource.mutate("post",`/engagements/${id}/post-archive-amendment`,v,"Amendemen dicatat");
    if(response) setDlg(null);
  };
  const rollForward = async (v) => {
    const response=await binderResource.mutate("post",`/engagements/${id}/roll-forward`,{...v,name:v.name || `Roll-forward ${v.period}`},
      r=>`Perikatan baru dibuat: ${r.data.rolled.accounts} akun, ${r.data.rolled.risks} risiko (REQUIRES REASSESSMENT)`);
    if(response) setDlg(null);
  };

  const amendFields = [
    { key: "object", label: "Objek yang Diubah", type: "text", required: true, placeholder: "mis. KKA Piutang, Catatan 12" },
    { key: "original_version", label: "Versi Asli", type: "text" },
    { key: "original_value", label: "Nilai Asli", type: "textarea" },
    { key: "revised_value", label: "Nilai Revisi", type: "textarea" },
    { key: "reason", label: "Alasan Amendemen", type: "textarea", required: true },
    { key: "reviewer", label: "Reviewer", type: "text" },
  ];
  const rfFields = [
    { key: "period", label: "Periode Tahun Berikutnya", type: "text", required: true, placeholder: "mis. 31 Desember 2026", span2: true },
    { key: "name", label: "Nama Perikatan Baru (opsional)", type: "text", span2: true },
  ];

  const RD_STATUS = ["Draft", "Prepared", "Manager Reviewed", "Partner Reviewed", "EQR Reviewed", "Final", "Locked"];
  return (
    <div className="space-y-3" data-testid="archive-center">
      {[binderResource.error,docsResource.error,amendsResource.error].filter(Boolean).length>0 && <div role="alert" className="text-sm text-red-700">Data arsip tidak dapat dimuat dengan akses saat ini. Muat ulang sebelum melanjutkan.</div>}
      <div className="bg-white border border-border rounded-sm p-3">
        <div className="font-head font-semibold text-sm flex items-center gap-1 mb-2"><FileCheck size={15} />Binder Perikatan</div>
        {binder && <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="border rounded-sm p-2">KKA: <b>{binder.wp_index.length}</b></div>
          <div className="border rounded-sm p-2">Bukti: <b>{binder.evidence_count}</b></div>
          <div className="border rounded-sm p-2">Dokumen Laporan: <b>{binder.report_documents}</b></div>
          <div className={`border rounded-sm p-2 ${binder.archive_ready ? "bg-green-50 border-green-300" : "bg-red-50 border-red-300"}`}>Siap Arsip: <b>{binder.archive_ready ? "Ya" : "Tidak"}</b></div>
        </div>}
        {binder && binder.blockers.length > 0 && <div className="text-[11px] text-red-700 mt-2" data-testid="archive-blockers">Blokir: {binder.blockers.join("; ")}</div>}
      </div>

      <div className="bg-white border border-border rounded-sm p-3 space-y-2">
        <div className="flex items-center justify-between"><span className="font-head font-semibold text-sm">Dokumen Laporan (Lifecycle)</span><Button size="sm" variant="outline" disabled={busy || !docsResource.data} onClick={createDoc} data-testid="rd-create">Buat Dokumen</Button></div>
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-2 text-xs border-b border-border/50 pb-1" data-testid={`rd-${d.id}`}>
            <span className="flex-1">{d.doc_type} · {d.language} · v{d.word_version} · <b>{d.status}</b></span>
            {d.status !== "Locked" && <select className="h-7 text-[11px] border rounded" disabled={busy} value={d.status} onChange={(e) => advance(d.id, e.target.value)} data-testid={`rd-status-${d.id}`}>{RD_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select>}
            {d.status === "Locked" && <span className="text-[10px] px-1.5 py-0.5 border rounded-sm bg-slate-100 text-slate-600 flex items-center gap-1"><Lock size={10} />Terkunci</span>}
          </div>
        ))}
        {docsResource.data && docs.length === 0 && <div className="text-[11px] text-muted-foreground">Belum ada dokumen laporan.</div>}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={busy || !binder} onClick={doArchive} data-testid="do-archive"><Archive size={13} className="mr-1" />Arsipkan & Kunci</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setDlg("amend")} data-testid="do-amend">Amendemen Pasca-Arsip</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setDlg("rf")} data-testid="do-rollforward"><GitBranch size={13} className="mr-1" />Roll-Forward Tahun Berikutnya</Button>
      </div>

      <RecordDialog open={dlg === "amend"} onOpenChange={(o) => !o && setDlg(null)} title="Amendemen Pasca-Arsip" description="Amendemen menyimpan nilai asli dan mencatat alasan, pengubah, dan reviewer." fields={amendFields} initial={{}} onSubmit={amend} testid="amend-dialog" />
      <RecordDialog open={dlg === "rf"} onOpenChange={(o) => !o && setDlg(null)} title="Roll-Forward ke Tahun Berikutnya" description="Menyalin info permanen + COA/mapping (saldo menjadi PY), menandai risiko REQUIRES REASSESSMENT. Kesimpulan/bukti/sign-off TIDAK disalin." fields={rfFields} initial={{}} onSubmit={rollForward} testid="rf-dialog" submitLabel="Buat Perikatan Baru" />

      {amends.length > 0 && <div className="bg-white border border-border rounded-sm p-3">
        <div className="text-xs font-head font-semibold mb-1">Amendemen Pasca-Arsip ({amends.length})</div>
        {amends.map((a) => <div key={a.id} className="text-[11px] text-muted-foreground">• {a.object}: {a.reason} — {a.changed_by} ({(a.at || "").slice(0, 10)})</div>)}
      </div>}

      <EvidencePanel id={id} phase="Arsip" module="Archive" />
    </div>
  );
}
